import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { useInMarketingShell } from './MarketingContext';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './BrandPage.css';

/**
 * BrandPage: /brand, CellCog brand guidelines v1 (CEL-3808, Oct 29 2026). Asked for by
 * Aditya via his AI employee Alexandra; Nitish: "you decide and create v1, Aditya will
 * review it and improve". Content source = Maeve's GUIDE_v1.md (the .md alternate at
 * public/brand/index.md carries the same text). Every logo file here derives from the
 * vector master marketing/assets/brand/master/cellcog-mark.svg (Iris, CEL-3809); the
 * finals live in Organization Drive/Brand Assets/brand_guidelines/final/.
 *
 * Logo specimens sit on FIXED light/dark grounds (they show a logo on its required
 * background), so they never follow the theme. Colours below must match
 * js/cellcog/src/styles/brandColors.json and the colorway SVG fills (mono-black is
 * #1d1d1f, not #000). Registrations: App.js route, adaptivePaths.js, react-snap,
 * seo.py STATIC_PAGES, public/brand/index.md, llms.txt (canon agent-readable-web).
 */

const ASSET = '/brand/img';
const PACK_URL = '/brand/CellCog-Logo-Pack.zip';
const PDF_URL = '/brand/CellCog-Brand-Guidelines.pdf';

const VERSIONS = [
  { key: 'black', name: 'Black', hex: '#1d1d1f', ground: 'light', use: 'Light backgrounds' },
  { key: 'white', name: 'White', hex: '#ffffff', ground: 'dark', use: 'Dark backgrounds' },
  { key: 'teal-light', name: 'Teal (light)', hex: '#2e7e7e', ground: 'light', use: 'Light backgrounds, accent' },
  { key: 'teal-dark', name: 'Teal (dark)', hex: '#7dc3c3', ground: 'dark', use: 'Dark backgrounds, accent' },
];

const BRAND_COLOURS = [
  { name: 'Teal, for light backgrounds', hex: '#2e7e7e', rgb: '46, 126, 126', cmyk: '63, 0, 0, 51' },
  { name: 'Teal, for dark backgrounds', hex: '#7dc3c3', rgb: '125, 195, 195', cmyk: '36, 0, 0, 24' },
];

const LIGHT_NEUTRALS = [
  { name: 'Ink (text, black logo)', hex: '#1d1d1f', rgb: '29, 29, 31', cmyk: '6, 6, 0, 88' },
  { name: 'Background', hex: '#ffffff', rgb: '255, 255, 255', cmyk: '0, 0, 0, 0' },
  { name: 'Surface', hex: '#f7f7f8', rgb: '247, 247, 248', cmyk: '0, 0, 0, 3' },
  { name: 'Muted text', hex: '#6e6e73', rgb: '110, 110, 115', cmyk: '4, 4, 0, 55' },
  { name: 'Hairline', hex: '#ececec', rgb: '236, 236, 236', cmyk: '0, 0, 0, 7' },
];

const DARK_NEUTRALS = [
  { name: 'Background', hex: '#161618', rgb: '22, 22, 24', cmyk: '8, 8, 0, 91' },
  { name: 'Surface', hex: '#1f1f21', rgb: '31, 31, 33', cmyk: '6, 6, 0, 87' },
  { name: 'Text', hex: '#f2f2f7', rgb: '242, 242, 247', cmyk: '2, 2, 0, 3' },
  { name: 'Muted text', hex: '#a1a1a6', rgb: '161, 161, 166', cmyk: '3, 3, 0, 35' },
  { name: 'Hairline', hex: '#2a2a2c', rgb: '42, 42, 44', cmyk: '5, 5, 0, 83' },
  { name: 'App tile', hex: '#141414', rgb: '20, 20, 20', cmyk: '0, 0, 0, 92' },
];

const MIN_SIZES = [
  { logo: 'Mark', screen: '16 px', print: '5 mm' },
  { logo: 'Wordmark', screen: '12 px', print: '3 mm' },
  { logo: 'Horizontal lockup', screen: '20 px', print: '6 mm' },
  { logo: 'Stacked lockup', screen: '48 px', print: '14 mm' },
];

const MISUSE = [
  'Don\u2019t stretch or squash the logo. Scale it evenly.',
  'Don\u2019t rotate or tilt it.',
  'Don\u2019t recolour it outside the palette (black, white, the two teals).',
  'Don\u2019t fill it with a gradient. The old gradient logo is retired.',
  'Don\u2019t add shadows, glows or bevels.',
  'Don\u2019t use it as an outline. It is always a solid fill.',
  'Don\u2019t put the black logo on a dark background. Use the white one there.',
  'Don\u2019t drop the dark app tile on a light page. Use the black mark.',
  'Don\u2019t place the logo over a busy part of a photo. Find a calm area.',
  'Don\u2019t redraw, retype or AI-generate a look-alike. Use the master files.',
];

const pad2 = (n) => String(n).padStart(2, '0');

const ColourTable = ({ rows, label }) => (
  <div className="brd__tablewrap">
    <table className="brd__table" aria-label={label}>
      <thead>
        <tr><th>Name</th><th>HEX</th><th>RGB</th><th>CMYK</th></tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={`${label}-${c.hex}`}>
            <td><span className="brd__chip" style={{ background: c.hex }} aria-hidden="true" />{c.name}</td>
            <td><code>{c.hex}</code></td>
            <td><code>{c.rgb}</code></td>
            <td><code>{c.cmyk}</code></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Specimen = ({ src, alt, ground, cap, imgClass }) => (
  <figure className="brd__tile">
    <div className={`brd__ground brd__ground--${ground}`}>
      <img src={src} alt={alt} className={imgClass} loading="lazy" />
    </div>
    <figcaption className="brd__cap">{cap}</figcaption>
  </figure>
);

const BrandPage = () => {
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();
  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/brand`;
  const title = 'CellCog brand guidelines: logo, colours and type';
  const description =
    'How to use the CellCog logo, colours and typography on the web, in decks, documents, social posts and email. Logo pack and PDF to download.';

  const trackDownload = (file) =>
    trackEvent('cta_click', { cta_name: 'brand_download', file, page: '/brand', location: 'brand' });

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: { '@type': 'WebSite', name: SEO_DEFAULTS.siteName, url: SEO_DEFAULTS.url },
          dateModified: '2026-10-29',
        }}
      />

      <div className={`aie-landing brand-page ${dark ? 'dark' : 'light'}`}>
        <div className="brd__inner">
          <header className="brd__head">
            <p className="brd__eyebrow">Brand guidelines</p>
            <h1 className="brd__title aie-display">The CellCog logo, colours and type</h1>
            <p className="brd__sub">
              Read this before you make anything that carries the CellCog name: a page, a deck,
              a document, a social post or an email. Always start from the files in the logo
              pack. Never draw, retype or regenerate the logo.
            </p>
            <div className="brd__downloads">
              <a className="brd__btn brd__btn--primary" href={PACK_URL} download onClick={() => trackDownload('logo_pack')}>
                Download the logo pack
              </a>
              <a className="brd__btn" href={PDF_URL} download onClick={() => trackDownload('guide_pdf')}>
                Download this guide (PDF)
              </a>
            </div>
          </header>

          <section className="brd__block" aria-labelledby="brd-logo">
            <h2 id="brd-logo" className="brd__h2">1. The logo</h2>
            <h3 className="brd__h3">Lockups</h3>
            <p className="brd__p">
              <strong>Horizontal (primary):</strong> the mark to the left of the wordmark. Use it by
              default: headers, slides, documents, email. <strong>Stacked:</strong> the mark above the
              wordmark, for square or tall spaces such as posters, cover pages, event screens and
              square social images.
            </p>
            <div className="brd__grid">
              <Specimen src={`${ASSET}/lockup-horizontal-black.svg`} alt="CellCog horizontal logo, black" ground="light" cap={<><strong>Horizontal</strong> · primary</>} />
              <Specimen src={`${ASSET}/lockup-stacked-black.svg`} alt="CellCog stacked logo, black" ground="light" cap={<><strong>Stacked</strong> · square and tall spaces</>} />
            </div>

            <h3 className="brd__h3">The mark and the wordmark</h3>
            <p className="brd__p">
              The C mark works on its own where space is tight or the CellCog name is already
              nearby: favicons, small footers, inner slides. The wordmark is &ldquo;CellCog&rdquo;
              set in Inter Tight Bold at -0.02em, converted to outlines. Use the outlined file;
              typing the word in a font is not the logo.
            </p>
            <div className="brd__grid">
              <Specimen src={`${ASSET}/mark-black.svg`} alt="CellCog mark, black" ground="light" imgClass="brd__img--mark" cap={<><strong>Mark</strong></>} />
              <Specimen src={`${ASSET}/wordmark-black.svg`} alt="CellCog wordmark, black" ground="light" cap={<><strong>Wordmark</strong></>} />
            </div>

            <h3 className="brd__h3">App icon</h3>
            <p className="brd__p">
              A white C on <code>#141414</code>, as a full square tile. Use it only where a platform
              expects an icon: browser tabs, app stores, profile avatars, marketplace listings. Never
              place the tile on a page or slide as a logo.
            </p>
            <div className="brd__grid">
              <Specimen src={`${ASSET}/app-icon.png`} alt="CellCog app icon: white C on a dark square" ground="light" imgClass="brd__img--tile" cap={<><strong>App icon</strong> · icon contexts only</>} />
            </div>
          </section>

          <section className="brd__block" aria-labelledby="brd-versions">
            <h2 id="brd-versions" className="brd__h2">2. Colour versions and backgrounds</h2>
            <p className="brd__p">
              <strong>A dark logo on a light background, a light logo on a dark background.</strong>{' '}
              Never dark on dark, never light on light. The two teals never swap: the deep teal
              belongs on light, the bright teal on dark. Black and white are the default; use teal
              only where the logo is meant to be an accent.
            </p>
            <div className="brd__grid brd__grid--4">
              {VERSIONS.map((v) => (
                <Specimen
                  key={v.key}
                  src={`${ASSET}/lockup-horizontal-${v.key}.svg`}
                  alt={`CellCog horizontal logo, ${v.name}`}
                  ground={v.ground}
                  cap={<><strong>{v.name}</strong> · <code>{v.hex}</code><br />{v.use}</>}
                />
              ))}
            </div>
            <p className="brd__note">
              Our &ldquo;black&rdquo; logo is <code>#1d1d1f</code>, a soft black, not pure <code>#000000</code>.
              On photographs, put the white logo over a calm, dark area or the black logo over a calm,
              light area. If there is no calm area, place the logo on a solid band.
            </p>
          </section>

          <section className="brd__block" aria-labelledby="brd-space">
            <h2 id="brd-space" className="brd__h2">3. Clear space</h2>
            <p className="brd__p">
              Keep empty space around every logo equal to <strong>2x</strong>, where x is the thickness
              of the C&rsquo;s outer ring at its top (0.14 of the mark&rsquo;s height), measured at the size
              the mark has in that logo. Nothing goes inside this space: no text, edges, images or other logos.
              The logo files are cropped tight to the artwork, so the clear space is not built in: add it yourself.
            </p>
            <figure className="brd__figure">
              <img src={`${ASSET}/clear-space.png`} alt="Clear space of 2x around the mark, the horizontal and the stacked logo" loading="lazy" />
            </figure>
          </section>

          <section className="brd__block" aria-labelledby="brd-min">
            <h2 id="brd-min" className="brd__h2">4. Minimum sizes</h2>
            <div className="brd__tablewrap">
              <table className="brd__table" aria-label="Minimum sizes">
                <thead><tr><th>Logo (height)</th><th>On screen</th><th>In print</th></tr></thead>
                <tbody>
                  {MIN_SIZES.map((m) => (
                    <tr key={m.logo}><td>{m.logo}</td><td>{m.screen}</td><td>{m.print}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="brd__note">Below these sizes, use the mark alone.</p>
            <figure className="brd__figure">
              <img src={`${ASSET}/min-sizes.png`} alt="Each logo rendered at its minimum size, true size and enlarged" loading="lazy" />
            </figure>
          </section>

          <section className="brd__block" aria-labelledby="brd-place">
            <h2 id="brd-place" className="brd__h2">5. Placement</h2>
            <ul className="brd__list">
              <li><strong>Slides (16:9).</strong> Cover: horizontal logo on the right, the headline or claim on the left. Inner slides: the mark alone in the same corner every time, small and quiet, or no logo. One logo per slide at most.</li>
              <li><strong>Documents and PDFs.</strong> Horizontal logo top left on the first page; the mark or nothing on later pages.</li>
              <li><strong>Web pages.</strong> Horizontal logo or wordmark top left in the header, linking home.</li>
              <li><strong>Social images.</strong> Horizontal logo in a corner with full clear space; stacked logo on square formats. The app icon is for profile avatars only.</li>
              <li><strong>Email.</strong> Horizontal logo top left in the header, 24 to 32 px tall.</li>
              <li><strong>Next to partner logos.</strong> Match the visual height of the other logo, keep clear space between them, and separate them with a thin vertical line if needed.</li>
            </ul>
          </section>

          <section className="brd__block" aria-labelledby="brd-misuse">
            <h2 id="brd-misuse" className="brd__h2">6. What not to do</h2>
            <div className="brd__misuse">
              {MISUSE.map((text, i) => (
                <figure key={text}>
                  <img src={`${ASSET}/misuse-${pad2(i + 1)}.png`} alt={`Misuse example ${i + 1}: ${text}`} loading="lazy" />
                </figure>
              ))}
            </div>
          </section>

          <section className="brd__block" aria-labelledby="brd-colour">
            <h2 id="brd-colour" className="brd__h2">7. Colour palette</h2>
            <h3 className="brd__h3">Brand colour</h3>
            <p className="brd__p">Teal is an accent: links, highlights, a key number, a badge. Never flood a page or slide with it.</p>
            <ColourTable rows={BRAND_COLOURS} label="Brand colour" />
            <h3 className="brd__h3">Neutrals, light theme</h3>
            <ColourTable rows={LIGHT_NEUTRALS} label="Neutrals, light theme" />
            <h3 className="brd__h3">Neutrals, dark theme</h3>
            <ColourTable rows={DARK_NEUTRALS} label="Neutrals, dark theme" />
            <p className="brd__note">
              CMYK values are converted from the screen colours. For printed brand pieces, ask the
              printer to match the HEX value on a proof before the run.
            </p>
          </section>

          <section className="brd__block" aria-labelledby="brd-type">
            <h2 id="brd-type" className="brd__h2">8. Typography</h2>
            <div className="brd__type">
              <div className="brd__typecard">
                <p className="brd__typesample brd__typesample--tight">Inter Tight</p>
                <p className="brd__note">Headlines and the wordmark. Bold or Semibold, letter-spacing about -0.02em.</p>
              </div>
              <div className="brd__typecard">
                <p className="brd__typesample brd__typesample--inter">Inter</p>
                <p className="brd__note">Body text, interfaces and most documents. Regular, 1.5 line height.</p>
              </div>
              <div className="brd__typecard">
                <p className="brd__typesample brd__typesample--playfair">Playfair Display</p>
                <p className="brd__note">Headlines in designed pieces only: investor decks and formal PDFs, with Inter for body.</p>
              </div>
            </div>
            <p className="brd__note">
              All three are free from Google Fonts. Never mix Playfair Display into product screens,
              web pages or social posts, and never use more than these three families.
            </p>
          </section>

          <section className="brd__block" aria-labelledby="brd-words">
            <h2 id="brd-words" className="brd__h2">9. Writing on brand materials</h2>
            <ul className="brd__list">
              <li>Plain and concrete. Say what the product does, with a real example where you can.</li>
              <li>No em dashes.</li>
              <li>Benchmark claims are always dated and linked to the source.</li>
              <li>Call our AI employees &ldquo;they&rdquo;, never &ldquo;it&rdquo;.</li>
            </ul>
          </section>

          <section className="brd__block" aria-labelledby="brd-agents">
            <h2 id="brd-agents" className="brd__h2">For AI agents making CellCog materials</h2>
            <p className="brd__p">
              Use only the files in the logo pack. Never generate, redraw or retype the logo with an
              image model or a font. Apply the background rule in section 2 before placing any logo,
              and check the result against section 6. A plain-text version of this page is at{' '}
              <a href="/brand/index.md">cellcog.ai/brand/index.md</a>.
            </p>
          </section>

          <section className="brd__block" aria-labelledby="brd-more">
            <h2 id="brd-more" className="brd__h2">Downloads and questions</h2>
            <ul className="brd__links">
              <li><a href={PACK_URL} download onClick={() => trackDownload('logo_pack')}>Logo pack (zip): mark, wordmark, both lockups and the app icon, in black, white and both teals; SVG, PNG, PDF and EPS</a></li>
              <li><a href={PDF_URL} download onClick={() => trackDownload('guide_pdf')}>This guide as a PDF</a></li>
              <li><Link to="/contact">Questions or suggestions</Link></li>
            </ul>
            <p className="brd__stamp">Version 1, October 29, 2026.</p>
          </section>
        </div>
      </div>
    </>
  );
};

export default BrandPage;
